/**
 * MetadataRoutes - Auto-generates routes from FERP Core metadata
 *
 * Uses DRR-065 metadata discovery - no hardcoded routes needed
 * One route handler works for ALL entity types across ALL subgraphs
 *
 * Pattern:
 * /entities/:subgraph/:entityType         → List page
 * /entities/:subgraph/:entityType/:id     → Detail page
 *
 * FERP Principles:
 * - Repository pattern: Data via useSubgraphEntityData (no direct fetch)
 * - Domain-agnostic: Same code for FHIR, ERP, Payroll, Legal
 * - Dumb frontend: Shape derived from SubgraphEntityType metadata
 * - Data-driven: Entity type config from useTenantSubgraphConfig
 */

import React, { useMemo, useCallback, Suspense } from 'react';
import { Routes, Route, useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Title,
  Button,
  Group,
  Stack,
  Text,
  Alert,
  Skeleton,
  LoadingOverlay,
  Badge,
  Paper,
} from '@mantine/core';
import { IconAlertCircle, IconChevronLeft, IconDatabase } from '@tabler/icons-react';
import { MetadataEntityTable } from '@ferp/subgraph-ui/components';
import { useTenantSubgraphConfig, type SubgraphEntityType } from '@/hooks/useTenantSubgraphConfig';
import { useSubgraphEntityData } from '@/hooks/useSubgraphEntityData';
import { useTenantId } from '@/auth/AuthContext';
import { useFormSchema } from '@/components/UniversalEntityForm/useFormSchema';
import { schemaToFormDefinition, mergeWithEntityData } from '@/components/UniversalEntityForm/schemaToFormDef';
import { FormRenderer } from '@/renderer/FormRenderer';

/**
 * Subgraph badge colors (domain-agnostic)
 */
const SUBGRAPH_COLORS: Record<string, string> = {
  fhir: 'blue',
  erp: 'orange',
  payroll: 'green',
  legal: 'violet',
  core: 'gray',
};

/**
 * Generic List Page - Works for ANY subgraph + entity type
 * Uses useSubgraphEntityData (proper data layer)
 */
function EntityListPage() {
  const { subgraph, entityType } = useParams() as {
    subgraph: string;
    entityType: string;
  };
  const navigate = useNavigate();
  const tenantId = useTenantId();
  const { getSubgraphConfig } = useTenantSubgraphConfig(tenantId ?? '');

  // Get entity type metadata from subgraph config
  const subgraphConfig = getSubgraphConfig(subgraph);
  const entityTypeConfig: SubgraphEntityType | null = useMemo(() => {
    if (!subgraphConfig) return null;
    return subgraphConfig.entityTypes.find(et => et.name === entityType) ?? null;
  }, [subgraphConfig, entityType]);

  // Fetch data via proper data layer
  const { data, loading, error } = useSubgraphEntityData(subgraph, entityTypeConfig);

  // Navigate to detail page on row click
  const handleRowClick = useCallback(
    (row: Record<string, unknown>) => {
      const id = row.id ?? row.resourceId ?? row[entityTypeConfig?.identifierFields[0] ?? 'id'];
      navigate(`/admin/${subgraph}/entities/${entityType}/${id}`);
    },
    [navigate, subgraph, entityType, entityTypeConfig]
  );

  // Derive display fields from entity type metadata
  const displayFields = useMemo(() => {
    if (!entityTypeConfig) return ['id', 'name', 'status'];

    const fields = new Set<string>(['id']);
    if (entityTypeConfig.displayField) fields.add(entityTypeConfig.displayField);
    for (const f of entityTypeConfig.identifierFields) fields.add(f);

    // Add common fields if present in data
    if (data.length > 0) {
      const firstRow = data[0];
      const common = ['name', 'status', 'createdAt', 'updatedAt'];
      for (const f of common) {
        if (f in firstRow && !fields.has(f)) fields.add(f);
      }
    }

    return [...fields].slice(0, 8); // Limit to 8 columns
  }, [entityTypeConfig, data]);

  // Handle missing entity type config
  if (!entityTypeConfig) {
    return (
      <Container size="xl" py="xl">
        <Alert icon={<IconAlertCircle size={16} />} title="Entity Type Not Found" color="yellow">
          <Text>
            Entity type <strong>{entityType}</strong> is not configured for the{' '}
            <strong>{subgraph}</strong> subgraph.
          </Text>
          <Text size="sm" c="dimmed" mt="xs">
            Check that the entity type is enabled in the SubgraphEntityManager.
          </Text>
        </Alert>
      </Container>
    );
  }

  const subgraphColor = SUBGRAPH_COLORS[subgraph] ?? 'gray';
  const subgraphLabel = subgraph.toUpperCase();

  return (
    <Container size="xl" py="xl">
      <Stack gap="lg">
        <Group justify="space-between" ta="left">
          <div>
            <Group gap="sm">
              <Title order={1}>{entityTypeConfig.pluralName ?? `${entityType}s`}</Title>
              <Badge
                leftSection={<IconDatabase size={12} />}
                color={subgraphColor}
                variant="light"
              >
                {subgraphLabel}
              </Badge>
            </Group>
            <Text c="dimmed" size="sm">
              {data.length} records
              {entityTypeConfig.description && ` \u2014 ${entityTypeConfig.description}`}
            </Text>
          </div>
          <Button onClick={() => navigate(`/admin/${subgraph}/entities/${entityType}/create`)}>
            New {entityType}
          </Button>
        </Group>

        <div style={{ position: 'relative' }}>
          <LoadingOverlay visible={loading} zIndex={1000} />
          {error && (
            <Alert icon={<IconAlertCircle size={16} />} title="Error" color="red">
              {error.message}
            </Alert>
          )}
          {!error && (
            <MetadataEntityTable
              subgraph={subgraph}
              entityType={entityType}
              data={data}
              onRowClick={handleRowClick}
              loading={loading}
              fields={displayFields}
            />
          )}
        </div>
      </Stack>
    </Container>
  );
}

/**
 * Generic Detail Page - Works for ANY subgraph + entity type
 *
 * Uses FormSchema → FormRenderer(readOnly) pipeline for proper field rendering.
 * Falls back to JSON display if no FormSchema exists.
 */
function EntityDetailPage() {
  const { subgraph, entityType, id } = useParams() as {
    subgraph: string;
    entityType: string;
    id: string;
  };
  const navigate = useNavigate();
  const tenantId = useTenantId();
  const { getSubgraphConfig } = useTenantSubgraphConfig(tenantId ?? '');

  // Get entity type metadata
  const subgraphConfig = getSubgraphConfig(subgraph);
  const entityTypeConfig: SubgraphEntityType | null = useMemo(() => {
    if (!subgraphConfig) return null;
    return subgraphConfig.entityTypes.find(et => et.name === entityType) ?? null;
  }, [subgraphConfig, entityType]);

  // Fetch FormSchema for this entity type
  const { schema: formSchema, loading: schemaLoading } = useFormSchema({
    subgraph,
    entityType,
    skip: !entityTypeConfig,
  });

  // Fetch entity data
  // TODO: Add single-entity fetch resolver for efficiency
  const { data, loading: dataLoading, error } = useSubgraphEntityData(subgraph, entityTypeConfig);

  const entity = useMemo(() => {
    if (!data.length) return null;
    return data.find(row => {
      const rowId = row.id ?? row.resourceId ?? row[entityTypeConfig?.identifierFields[0] ?? 'id'];
      return String(rowId) === id;
    }) ?? null;
  }, [data, id, entityTypeConfig]);

  // Convert FormSchema to FormDefinition with entity data
  const formDefinition = useMemo(() => {
    if (!formSchema || !entity) return null;
    const baseDef = schemaToFormDefinition(formSchema);
    return mergeWithEntityData(baseDef, entity as Record<string, unknown>);
  }, [formSchema, entity]);

  const loading = schemaLoading || dataLoading;
  const subgraphColor = SUBGRAPH_COLORS[subgraph] ?? 'gray';
  const subgraphLabel = subgraph.toUpperCase();

  if (!entityTypeConfig) {
    return (
      <Container size="lg" py="xl">
        <Alert icon={<IconAlertCircle size={16} />} title="Entity Type Not Found" color="yellow">
          <Text>
            Entity type <strong>{entityType}</strong> is not configured for the{' '}
            <strong>{subgraph}</strong> subgraph.
          </Text>
        </Alert>
      </Container>
    );
  }

  if (error) {
    return (
      <Container size="lg" py="xl">
        <Alert icon={<IconAlertCircle size={16} />} title="Error" color="red">
          {error.message}
        </Alert>
      </Container>
    );
  }

  // Get display name from entity data
  const displayField = entityTypeConfig.displayField ?? 'name';
  const entityDisplayName = entity?.[displayField] ?? entity?.id ?? id;

  return (
    <Container size="lg" py="xl">
      <Stack gap="lg">
        <Group gap="xs">
          <Button
            variant="subtle"
            leftSection={<IconChevronLeft size={16} />}
            onClick={() => navigate(`/admin/${subgraph}/entities/${entityType}`)}
          >
            Back to {entityTypeConfig.pluralName ?? `${entityType}s`}
          </Button>
          <Badge
            leftSection={<IconDatabase size={12} />}
            color={subgraphColor}
            variant="light"
          >
            {subgraphLabel}
          </Badge>
        </Group>

        <Title order={2}>{String(entityDisplayName)}</Title>

        {loading && <Skeleton height={400} radius="md" />}

        {!loading && !entity && (
          <Alert icon={<IconAlertCircle size={16} />} title="Not Found" color="yellow">
            <Text>
              {entityType} with ID <strong>{id}</strong> not found.
            </Text>
          </Alert>
        )}

        {!loading && entity && formDefinition && (
          <Paper p="md" withBorder>
            <FormRenderer
              form={formDefinition}
              readOnly={true}
              onSubmit={() => Promise.resolve()}
            />
          </Paper>
        )}

        {!loading && entity && !formDefinition && (
          <Paper p="md" withBorder>
            <Alert icon={<IconAlertCircle size={16} />} title="No Form Schema" color="blue" mb="md">
              <Text size="sm">
                No FormSchema defined for {subgraph}/{entityType}. Showing raw data.
              </Text>
            </Alert>
            <pre style={{ fontSize: '12px', overflow: 'auto', maxHeight: '400px' }}>
              {JSON.stringify(entity, null, 2)}
            </pre>
          </Paper>
        )}
      </Stack>
    </Container>
  );
}

/**
 * Main Routes Handler
 *
 * Single route handler for all subgraphs and entity types
 */
export function MetadataRoutes() {
  return (
    <Routes>
      <Route path="/entities/:subgraph/:entityType" element={<EntityListPage />} />
      <Route path="/entities/:subgraph/:entityType/:id" element={<EntityDetailPage />} />
      <Route
        path="/*"
        element={
          <Container py="xl">
            <Alert icon={<IconAlertCircle size={16} />} title="Route Not Found" color="yellow">
              Please select a subgraph and entity type to view data.
            </Alert>
          </Container>
        }
      />
    </Routes>
  );
}
